import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { BeforeMount, Editor, OnMount, OnValidate, useMonaco } from '@monaco-editor/react';
import dirtyJson from "dirty-json";
import * as Monaco from "monaco-editor/esm/vs/editor/editor.api";

import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";

import { useToggle } from '../../hooks';
import { ErrorMessageBar } from './components/error-message-bar';
import { ToolBar } from './components/tool-bar';
import { SwaggerEditorStyles } from './styles';
import { downloadJsonFile, minifyJsonString, prettifyJsonString } from './utils';
import Schema from './utils/open-api-v3-schema.json';


interface RefObject extends Monaco.editor.ICodeEditor {
    _domElement?: HTMLElement;
}

interface SwaggerUiEditorProps {
    value: string;
    title?: string;
    onChange: (value: string | undefined) => void;
}

const SwaggerUiEditor: React.FC<SwaggerUiEditorProps> = ({
    value,
    title,
    onChange,
}) => {

    const monaco = useMonaco();
    const editorRef = useRef<RefObject | null>(null);

    const [isValidJson, setIsValidJson] = useState(false);
    const [isAutoPrettifyOn, toggleAutoPrettifyOn] = useToggle(false);
    const [errors, setErrors] = useState<string[]>([]);

    const handleClearClick = () => editorRef.current?.setValue("");
    const handleEditorWillMount: BeforeMount = () => handleJsonSchemasUpdate();

    const handleJsonSchemasUpdate = useCallback(() => {
        const parsedSchemaData = Schema;
        console.log("Parsed schema data:", parsedSchemaData);
        monaco?.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: true,
            schemas: parsedSchemaData
                ? [
                    {
                        uri: window.location.href,
                        fileMatch: ["*"],
                        schema: parsedSchemaData,
                    },
                ]
                : undefined,
        });
    }, [monaco]);

    const handleDownloadClick = () => {
        const value = editorRef.current?.getValue();
        value && downloadJsonFile(value);
    };

    const handleMinifyClick = () => {
        const editor = editorRef.current;
        if (!editor) return;
        const value = editor.getValue();
        const minifiedValue = minifyJsonString(value);
        editor.setValue(minifiedValue);
    };

    const handleEditorPrettify = useCallback(() => {
        editorRef.current?.getAction("editor.action.formatDocument")?.run();
    }, []);

    const handleUploadClick = (file: File) => {
        const fileReader = new FileReader();
        fileReader.onload = () => {
            const result = fileReader.result as string;
            handleEditorUpdateValue(result);
        };
        fileReader.readAsText(file);
    };

    const handleEditorUpdateValue = useCallback((value?: string) => {
        const editor = editorRef.current;
        if (editor) {
            editor.setValue(value || "");
            value && editor?.getAction("editor.action.formatDocument")?.run();
        }
    }, []);

    const handleFixClick = () => {
        const editor = editorRef.current;
        const value = editor && editor.getValue();
        const fixedValue = value && dirtyJson.parse(value);
        const formattedValue = fixedValue && prettifyJsonString(JSON.stringify(fixedValue));
        editor && editor.setValue(formattedValue);
    };

    const handleEditorDidMount: OnMount = (editor) => {
        editorRef.current = editor;

        editor.getModel()?.updateOptions({ tabSize: 2, insertSpaces: false });
        updateEditorLayout();

        window.addEventListener("resize", () => {
            updateEditorLayout();
        });

        // value && handleEditorUpdateValue(prettifyJsonString(value));
    };

    const updateEditorLayout = () => {
        const editor: any = editorRef.current;
        if (editor) {
            const editorEl = editor._domElement;
            if (editorEl) {
                const { width, height } = editorEl.getBoundingClientRect();
                editor.layout({ width, height });
            }
        }
    }

    const handleEditorChange = useCallback((value: string | undefined) => {
        isAutoPrettifyOn && handleEditorPrettify();
        onChange(value);
    }, [handleEditorPrettify, isAutoPrettifyOn, onChange]);

    const handleEditorValidation: OnValidate = useCallback((markers) => {
        const errorMessage = markers.map(
            ({ startLineNumber, message }) => `line ${startLineNumber}: ${message}`
        );
        const hasContent = editorRef.current?.getValue();
        const hasError: boolean = errorMessage.length > 0;
        setIsValidJson(!!hasContent && !hasError);
        setErrors(errorMessage);
    }, []);

    useEffect(() => {
        updateEditorLayout();
    }, []);

    useEffect(() => {
        handleJsonSchemasUpdate();

        return () => { }
    }, [handleJsonSchemasUpdate])


    return (
        <Box
            sx={SwaggerEditorStyles.container}
        >
            {title && (
                <Typography sx={{ m: 2 }} variant="h4">
                    {title}
                </Typography>
            )}

            {value && (
                <Box sx={SwaggerEditorStyles.toolbar}>
                    <ToolBar
                        isValidJson={isValidJson}
                        isAutoPrettifyOn={isAutoPrettifyOn}
                        onAutoPrettifyChange={toggleAutoPrettifyOn}
                        onClearClick={handleClearClick}
                        onDownloadClick={handleDownloadClick}
                        onMinifyClick={handleMinifyClick}
                        onPrettifyClick={handleEditorPrettify}
                        onUploadClick={handleUploadClick}
                        onFixClick={handleFixClick}
                        onShowSchemaChange={function (): void {
                            throw new Error("Function not implemented.");
                        }}
                    />
                </Box>
            )}

            <Box sx={SwaggerEditorStyles.editorWrapper}>
                <Box sx={SwaggerEditorStyles.monacoEditorContainer}>
                    <Box sx={SwaggerEditorStyles.monacoEditorWrapper}>
                        <Editor
                            language="json"
                            options={{
                                automaticLayout: true,
                                autoClosingBrackets: "always",
                                autoClosingQuotes: "always",
                                formatOnPaste: true,
                                formatOnType: true,
                                scrollBeyondLastLine: false,
                            }}
                            value={value}
                            onMount={handleEditorDidMount}
                            onChange={handleEditorChange}
                            beforeMount={handleEditorWillMount}
                            onValidate={handleEditorValidation}
                        />
                    </Box>
                    <Box sx={SwaggerEditorStyles.monacoError}>
                        {value && <ErrorMessageBar errors={errors} />}
                    </Box>
                </Box>

                <Box sx={SwaggerEditorStyles.swaggerContainer}>
                    <Box sx={SwaggerEditorStyles.swaggerWrapper}>
                        <SwaggerUI
                            spec={value}
                        // url="https://petstore.swagger.io/v2/swagger.json"
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default SwaggerUiEditor